import { Component, OnInit, inject, signal } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IimAutocompleteFilterable } from '@iim-angular/components';
import { AddressDialogOptions, AddressDialogResult, AddressType } from '../model/basket.model';
import {
    Address,
    BasketAddress,
    Company,
    Customer
} from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/ui';
import { coalesceToNull } from '@lib/util';
import { DeliveryContactCandidate, DeliveryContactCandidateDataService } from '@lib/shared/basket-service';

/** EPROC-11920: the contact-person form — the two writable fields of the contact-only mode. */
interface DeliveryContactForm {
    firstName: FormControl<string | null>;
    lastName: FormControl<string | null>;
}

/**
 * EPROC-11920: wraps a colleague for local filtering in the autocomplete. The candidate list is
 * bounded to one tenant and booking circle, so filtering client-side beats a round-trip per
 * keystroke — same approach as the "Bestellen im Namen von" target dialog.
 */
class DeliveryContactCandidateFilterable implements IimAutocompleteFilterable<DeliveryContactCandidateFilterable> {
    constructor(private readonly candidate: DeliveryContactCandidate) {}

    public matches(value: string | IimAutocompleteFilterable<DeliveryContactCandidateFilterable>): boolean {
        if (value instanceof DeliveryContactCandidateFilterable) {
            return this.equals(value);
        }
        return this.candidate.displayName.toLowerCase().includes(String(value).toLowerCase());
    }

    public get value(): DeliveryContactCandidateFilterable {
        return this;
    }

    public get valueDisplay(): string {
        return this.candidate.displayName;
    }

    public equals(
        identifier: string | number | IimAutocompleteFilterable<DeliveryContactCandidateFilterable>,
    ): boolean {
        if (identifier instanceof DeliveryContactCandidateFilterable) {
            return identifier.candidate.displayName === this.candidate.displayName;
        }
        return identifier !== undefined && identifier.toString() === this.candidate.displayName;
    }

    public get contact(): DeliveryContactCandidate {
        return this.candidate;
    }
}

interface DeliveryAddressForm {
    firstName: FormControl<string | null>;
    lastName: FormControl<string | null>;
    companyName: FormControl<string | null>;
    companyName2: FormControl<string | null>;
    street: FormControl<string | null>;
    zip: FormControl<string | null>;
    city: FormControl<string | null>;
    country: FormControl<string | null>;
}

@Component({
    selector: 'basket-address-selection',
    templateUrl: './basket-address-selection.component.html',
    standalone: false
})
export class BasketAddressSelectionComponent implements OnInit {
    data = inject<AddressDialogOptions>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject<MatDialogRef<BasketAddressSelectionComponent>>(MatDialogRef);
    private readonly contactCandidateDataService = inject(DeliveryContactCandidateDataService);

    options!: AddressDialogOptions;
    filteredAddresses!: BasketAddress[];

    addressForm: FormGroup<DeliveryAddressForm> | undefined;
    /** EPROC-11920: set while the contact-only mode is open; mutually exclusive with addressForm. */
    contactForm: FormGroup<DeliveryContactForm> | undefined;
    contactAddress: BasketAddress | undefined;
    addressSearchCtrl: UntypedFormControl = new UntypedFormControl('');

    readonly contactCandidates = signal<DeliveryContactCandidateFilterable[]>([]);
    readonly contactCandidatesLoading = signal(false);
    readonly contactCandidatesFailed = signal(false);

    addressSearchPlaceholder = 'Rechnungsadresse suchen';
    isDelivery = false;

    ngOnInit(): void {
        this.options = this.data;
        this.filteredAddresses = this.options.availableAddresses;
        this.initSearch();
        if (this.options.addressType === AddressType.DELIVERY_ADDRESS) {
            this.addressSearchPlaceholder = 'Lieferadresse suchen';
            this.isDelivery = true;
        }
    }

    cancel(): void {
        this.dialogRef.close();
    }

    save(address: BasketAddress) {
        // Default to a predefined-list selection so the backend does not fall back to the
        // "saved or edited" path because of an undefined flag. The default comes before the
        // spread so an explicit savedOrEdited (e.g. true from saveFreeEdit) is preserved.
        this.dialogRef.close({
            savedOrEdited: false,
            ...address,
        });
    }

    freeEdit(address?: BasketAddress) {
        this.addressForm = this.buildAddressForm(address);
    }

    /**
     * EPROC-11920: opens the contact-only mode for the address given (the current one). The address
     * fields are rendered from {@link contactAddress} for checking and stay read-only, so this mode
     * cannot change anything but the name.
     */
    contactEdit(address: BasketAddress): void {
        this.contactAddress = address;
        const customer = address?.contactInfo?.customerInfo;
        this.contactForm = new FormGroup<DeliveryContactForm>({
            firstName: new FormControl<string | null>(coalesceToNull(customer?.firstName), [
                Validators.maxLength(40),
            ]),
            lastName: new FormControl<string | null>(coalesceToNull(customer?.lastName), [Validators.maxLength(40)]),
        });
        this.loadContactCandidates();
    }

    closeContactEdit(): void {
        this.contactForm = undefined;
        this.contactAddress = undefined;
    }

    onContactCandidateSelected(option: IimAutocompleteFilterable<DeliveryContactCandidateFilterable>): void {
        if (!(option instanceof DeliveryContactCandidateFilterable) || !this.contactForm) {
            return;
        }
        // The picked colleague only pre-fills the two fields; they stay editable afterwards, per AK.
        this.contactForm.patchValue({
            firstName: option.contact.firstName,
            lastName: option.contact.lastName,
        });
        this.contactForm.markAsDirty();
    }

    /**
     * Hands back the current address with the new name and the {@code contactOnly} marker, so the
     * store sends the name alone and the master-data id of the address survives.
     */
    saveContactEdit(): void {
        if (!this.contactForm?.valid || !this.contactAddress) {
            return;
        }
        const result: AddressDialogResult = {
            ...this.contactAddress,
            contactInfo: {
                ...this.contactAddress.contactInfo,
                customerInfo: {
                    ...this.contactAddress.contactInfo?.customerInfo,
                    firstName: this.contactForm.controls.firstName.value ?? '',
                    lastName: this.contactForm.controls.lastName.value ?? '',
                } as Customer,
            },
            savedOrEdited: false,
            contactOnly: true,
        } as AddressDialogResult;

        this.dialogRef.close(result);
    }

    private loadContactCandidates(): void {
        this.contactCandidatesLoading.set(true);
        this.contactCandidatesFailed.set(false);
        this.contactCandidateDataService.listCandidates(this.options.companyCodeId).subscribe({
            next: (candidates) => {
                this.contactCandidates.set(
                    candidates.map((candidate) => new DeliveryContactCandidateFilterable(candidate)),
                );
                this.contactCandidatesLoading.set(false);
            },
            error: () => {
                this.contactCandidatesLoading.set(false);
                this.contactCandidatesFailed.set(true);
            },
        });
    }

    // eslint-disable-next-line complexity
    private buildAddressForm(address?: BasketAddress): FormGroup<DeliveryAddressForm> {
        const customer = address?.contactInfo?.customerInfo;
        const company = address?.contactInfo?.companyInfo;
        const addr = address?.address;

        return new FormGroup<DeliveryAddressForm>({
            firstName: new FormControl<string | null>(coalesceToNull(customer?.firstName)),
            lastName: new FormControl<string | null>(coalesceToNull(customer?.lastName)),
            companyName: new FormControl<string | null>(coalesceToNull(company?.name), [Validators.required]),
            companyName2: new FormControl<string | null>(coalesceToNull(company?.name2)),
            street: new FormControl<string | null>(coalesceToNull(addr?.street), [Validators.required]),
            zip: new FormControl<string | null>(coalesceToNull(addr?.zip), [
                Validators.required,
                Validators.pattern(/^\d+$/),
                Validators.maxLength(10),
            ]),
            city: new FormControl<string | null>(coalesceToNull(addr?.city), [Validators.required]),
            country: new FormControl<string | null>(coalesceToNull(addr?.country?.value)),
        });
    }

    saveFreeEdit(): void {
        if (this.addressForm?.valid) {
            const address = {
                contactInfo: {
                    companyInfo: {
                        name: this.addressForm.controls.companyName.value,
                        name2: this.addressForm.controls.companyName2.value,
                    } as Company,
                    customerInfo: {
                        firstName: this.addressForm.controls.firstName.value,
                        lastName: this.addressForm.controls.lastName.value,
                    } as Customer,
                },
                address: {
                    country: {
                        value: this.addressForm.controls.country.value,
                    },
                    city: this.addressForm.controls.city.value,
                    zip: this.addressForm.controls.zip.value,
                    street: this.addressForm.controls.street.value,
                } as Address,
                savedOrEdited: true,
            } as BasketAddress;

            this.save(address);
        }
    }

    private initSearch(): void {
        this.addressSearchCtrl.valueChanges.subscribe((value: string) => {
            const q = (value ?? '').toLowerCase().trim();
            if (!q) {
                this.filteredAddresses = this.options.availableAddresses;
                return;
            }
            this.filteredAddresses = this.options.availableAddresses.filter((el) => this.matchesQuery(el, q));
        });
    }

    private matchesQuery(el: BasketAddress, q: string): boolean {
        const fields = [
            el.contactInfo?.customerInfo?.firstName,
            el.contactInfo?.customerInfo?.lastName,
            el.contactInfo?.companyInfo?.name,
            el.contactInfo?.companyInfo?.name2,
            el.address?.street,
            el.address?.zip,
            el.address?.city,
            el.address?.country?.value,
        ];
        return fields.some((v) => v?.toLowerCase().includes(q));
    }
}
